import React from 'react';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services/ifys-services' },
  { label: 'Child Protection' },
];

const SocialIcons = ({ facebook, instagram, name }) => (
  <div className="flex gap-3 mt-4">
    <a
      href={facebook}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name} on Facebook`}
      title={`${name} on Facebook`}
      className="text-ifys-blue hover:text-ifys-blue/70 transition-colors focus:outline-none focus:ring-2 focus:ring-ifys-blue rounded"
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
    </a>
    <a
      href={instagram}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name} on Instagram`}
      title={`${name} on Instagram`}
      className="text-ifys-blue hover:text-ifys-blue/70 transition-colors focus:outline-none focus:ring-2 focus:ring-ifys-blue rounded"
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    </a>
  </div>
);

const ChildProtectionPage = () => {
  return (
    <PageLayout activeLink="services">
      <PageHero
        title="Child Protection"
        backgroundImage="/images/thumb3.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6">
          Child Protection
        </h2>
      </section>

      {/* Foster Care */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">Foster Care</h3>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              IFYS has been providing foster and kinship care services to children and young people,
              in the care of the Department of Child Safety, since 1992.
            </p>
            <p>
              Our service is contracted to support children and young people who live locally on the
              Sunshine Coast and Cairns region. As all children and young people are different and
              have unique needs, so is the kind of care and support that is needed.
            </p>
            <p>
              If you are interested in becoming a Foster Carer, visit{' '}
              <a href="https://www.fostercare.com.au" className="text-ifys-blue font-semibold hover:underline" target="_blank" rel="noopener noreferrer">
                www.fostercare.com.au
              </a>{' '}
              to find out more.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h4 className="font-bold text-ifys-blue mb-2">Foster Care Sunshine Coast</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Foster and kinship care across the Sunshine Coast region.
              </p>
              <SocialIcons
                name="IFYS Foster Care"
                facebook="https://www.facebook.com/IFYSFosterCare"
                instagram="https://www.instagram.com/ifysfostercare/"
              />
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h4 className="font-bold text-ifys-blue mb-2">Foster Care Cairns</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Foster and kinship care across the Cairns and Far North Queensland region.
              </p>
              <SocialIcons
                name="Foster Care Cairns"
                facebook="https://www.facebook.com/fostercarecairns"
                instagram="https://www.instagram.com/fostercarecairns/"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Residential Care */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">Residential Care</h3>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              IFYS host multiple Residential Care services across Queensland spanning from South
              Queensland through to far North Queensland and continues to develop new services to
              support our most vulnerable young people in the Child Protection system.
            </p>
            <p>
              IFYS&rsquo; vision is to support and strengthen communities by providing sustainable
              and purposeful service delivery; IFYS recruits to the needs of the services required
              and encourages diversity that reflects the diversity of young people in care.
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ChildProtectionPage;
